import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'packages/debounce';
import cookieUtil from 'tradera/utils/cookie';
import { useOnMount } from 'tradera/hooks/use-on-mount';
import { VIEWPORT_WIDTH } from 'tradera/constants/cookies';
import { isServer } from 'tradera/utils/nextjs';
import { getBreakpointFromWidth } from 'tradera/utils/breakpoint';
import { GtmService } from 'tradera/services/google-tagmanager/google-tagmanager-service';
import { setHasReportedViewportDiff } from 'tradera/state/status/reducer';

const setViewportCookie = (viewportWidth) => {
    const expires = new Date();
    expires.setMonth(expires.getMonth() + 12);
    cookieUtil.createCookie(VIEWPORT_WIDTH, viewportWidth, expires);
};

const viewportChangeCallback = debounce(() => {
    if (document.visibilityState === 'visible') {
        const viewportWidth = String(isServer ? 0 : window.innerWidth);
        const viewportWidthCookie = cookieUtil.readCookie(VIEWPORT_WIDTH);

        const shouldUpdateCookie = viewportWidth !== viewportWidthCookie;
        if (shouldUpdateCookie) {
            setViewportCookie(viewportWidth);
        }
    }
}, 300);

export const useViewportWidthCookie = (enabled = true) =>
    useOnMount(() => {
        if (!enabled) {
            return;
        }

        viewportChangeCallback();
        window.addEventListener('resize', viewportChangeCallback);
        document.addEventListener('visibilitychange', viewportChangeCallback);
        return () => {
            window.removeEventListener('resize', viewportChangeCallback);
            document.removeEventListener(
                'visibilitychange',
                viewportChangeCallback
            );
        };
    });

export const useResponsiveDevice = (ssrViewportWidth) => {
    const [responsiveDevice, setResponsiveDevice] = useState({
        // Render with same viewport during hydration as on server
        // Prevents "Warning: Expected server HTML to contain a matching..."
        width: ssrViewportWidth
    });
    const dispatch = useDispatch();
    const hasReportedViewportDiff = useSelector(
        (state) => state.status.hasReportedViewportDiff
    );

    useOnMount(() => {
        const ssrBreakpoint = getBreakpointFromWidth(ssrViewportWidth);
        const clientBreakpoint = getBreakpointFromWidth(window.innerWidth);

        if (ssrBreakpoint !== clientBreakpoint && !hasReportedViewportDiff) {
            GtmService.trackAction(
                'Viewport breakpoint',
                'SSR and CSR diff',
                `${ssrBreakpoint} -> ${clientBreakpoint}`
            );
        }
        dispatch(setHasReportedViewportDiff(true));
        // Enables react-responsive to use window.mediaQuery after hydration
        setResponsiveDevice(undefined);
    });
    return responsiveDevice;
};
